import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { findManual, getManualFileUrl, MANUAL_STATUS } from '../config/manuals';
import { isAuthenticated } from '../services/authService';

/**
 * 매뉴얼 뷰어.
 * 매뉴얼 HTML(public/manuals/*.html)을 iframe 으로 띄운다.
 * iframe 을 쓰는 이유: 매뉴얼 문서가 자체 CSS 를 가지고 있어서
 * 앱(Tailwind/antd) 스타일과 서로 간섭하지 않게 격리하기 위함.
 */
const ManualViewer = () => {
  const { manualId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // 비로그인 상태에서도 매뉴얼은 볼 수 있다. 다만 실제 메뉴로 가는 버튼은 로그인해야 의미가 있다.
  const authed = isAuthenticated();

  const manual = findManual(manualId);
  const isReady = manual && manual.status === MANUAL_STATUS.READY;
  // ?c=c3 으로 장을 지정하면 그 장부터 보여준다. AI 도우미가 "이 장을 보세요" 하고 거는 링크가 이 모양이다.
  // 장 id 는 매뉴얼 HTML 안의 앵커라 iframe 주소에 #으로 붙인다.
  const chapter = (searchParams.get('c') || '').replace(/[^A-Za-z0-9_-]/g, '');
  const baseUrl = manual ? getManualFileUrl(manual.id) : '';
  const fileUrl = baseUrl && chapter ? `${baseUrl}#${chapter}` : baseUrl;

  const handlePrint = () => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (!win) return;
    try {
      win.focus();
      win.print();
    } catch (e) {
      window.open(fileUrl, '_blank', 'noopener');
    }
  };

  if (!manual) {
    return (
      <div className="p-4">
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
          <p className="text-sm text-gray-600">요청한 매뉴얼을 찾을 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/manuals')}
            className="mt-4 inline-flex items-center px-3.5 py-1.5 text-xs rounded bg-primary-600 text-white hover:bg-primary-700"
          >
            매뉴얼 목록으로
          </button>
        </div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="p-4">
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-800">{manual.title}</span> 매뉴얼은 준비 중입니다.
          </p>
          <button
            type="button"
            onClick={() => navigate('/manuals')}
            className="mt-4 inline-flex items-center px-3.5 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            매뉴얼 목록으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/manuals')}
            className="inline-flex items-center gap-1 px-2.5 h-8 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            목록
          </button>
          <div className="min-w-0">
            <div className="text-base font-semibold text-gray-900 truncate">
              {manual.title} 매뉴얼
            </div>
            {manual.updatedAt && (
              <div className="text-[11px] text-gray-400">{manual.updatedAt} 기준</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {authed && manual.menuPath && (
            <button
              type="button"
              onClick={() => navigate(manual.menuPath)}
              className="inline-flex items-center px-2.5 h-8 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              {manual.menuLabel} 열기
            </button>
          )}
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1 px-2.5 h-8 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          >
            <PrinterIcon className="w-3.5 h-3.5" />
            인쇄
          </button>
          <button
            type="button"
            onClick={() => window.open(fileUrl, '_blank', 'noopener')}
            className="inline-flex items-center gap-1 px-2.5 h-8 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          >
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            새 탭
          </button>
        </div>
      </div>

      {/* 앱 레이아웃이 본문 높이를 고정해 주지 않으므로 뷰포트 기준으로 잡는다 */}
      <div
        className="relative rounded-xl border border-gray-200 bg-white overflow-hidden"
        style={{ height: 'calc(100vh - 220px)', minHeight: 420 }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
            매뉴얼을 불러오는 중...
          </div>
        )}
        <iframe
          ref={iframeRef}
          title={`${manual.title} 매뉴얼`}
          src={fileUrl}
          className="w-full h-full block"
          style={{ border: 0 }}
          onLoad={() => setLoading(false)}
        />
      </div>
    </div>
  );
};

export default ManualViewer;
